// User-defined attributes (additional attributes) — the shared shapes.
//
// One definition of the layer document, the `uda_resolve` answer and the setup
// TRANSPORT every host implements (platform: frontend/src/livepreview/runtime/
// udaTransport.ts; downloaded runtime + J2EE: runtime/client/src/runtime/uda.ts).
// Mirrors the engine: twasta_runtime_engine/uda/{model,resolve,service}.py and
// docs/plans/user-defined-attributes-implementation.md §2.
import type { ColumnDef } from '../txn/types'

/** A rule value: Any, a list of codes, or every value but these. */
export type UdaRuleValue = '*' | string[] | { any_except: string[] }

/** One "who gets it" rule: a value per deciding-column key (missing = Any). */
export type UdaRule = Record<string, UdaRuleValue>

/** Where a deciding value comes from: a property of the master a lookup column
 *  of this form (`form.<col>`) or of the header (`header.<col>`) points at, or a
 *  column of this form. */
export type UdaDriverSource =
  | { kind: 'master'; via: string; column: string }
  | { kind: 'form'; column: string }

export interface UdaDriver {
  key: string
  label?: string
  from: UdaDriverSource
  origin?: 'design' | 'tenant' | string
}

/** An attribute column: the transaction designer's ColumnDef keys plus the
 *  attribute-only ones. `tenant_may` lists the ColumnDef keys a tenant may
 *  change on a STANDARD (design) column, verbatim. */
/** A picture (an uploaded asset's URL) or a colour swatch per stored value —
 *  the drawer's "matching picture" and the grid's swatch dot. */
export interface UdaPictures {
  by_value?: Record<string, { swatch?: string; asset?: string; caption?: string }>
  [key: string]: unknown
}

export interface UdaColumn extends Partial<Omit<ColumnDef, 'name' | 'in_grid'>> {
  name: string
  in_grid?: boolean
  pictures?: UdaPictures
  origin?: 'design' | 'tenant' | string
  tenant_may?: string[]
  retired?: boolean
}

export interface UdaSet {
  key: string
  label?: string
  color?: number | null
  placement?: { group: string | null }
  rules?: UdaRule[]
  columns: UdaColumn[]
  origin?: 'design' | 'tenant' | string
  tenant_may?: string[]
  hidden?: boolean
}

export interface UdaFlag {
  key: string
  name?: string
  icon?: string
  color?: string
  /** An expression over the record (`{form.uda_x} > 24`). */
  when?: string
  show_on?: Array<'header_badge' | 'list_column' | 'line_row' | string>
  origin?: 'design' | 'tenant' | string
  tenant_may?: string[]
  hidden?: boolean
}

export interface UdaForm {
  enabled?: boolean
  group?: string
  drivers: UdaDriver[]
  sets: UdaSet[]
  flags: UdaFlag[]
  /** Design layer only. */
  allow_tenant_additions?: boolean
  ask_by?: Array<{ via: string; column: string; group_by?: string }>
}

/** The layer document (design `transaction.uda` / a tenant's body). */
export interface UdaLayer {
  version: number
  forms: Record<string, UdaForm>
  allow_tenant_additions?: boolean
}

/** The marker an injected column carries (D5). */
export interface UdaColumnMarker {
  key: string
  set_keys?: string[]
  origin?: string
  def_version?: number
  driver?: boolean
  /** A deciding value's source (driver columns only). */
  from?: UdaDriverSource
  /** The rule leaves (`item_change` / `validations`) the ORGANIZATION wrote:
   *  their functions are its own (read-only, attributes only). */
  tenant_code?: string[]
}

/** A ColumnDef as `uda_resolve` returns it (an injected column). */
export type UdaColumnDef = ColumnDef & { uda: UdaColumnMarker }

export interface UdaSetInfo {
  key: string
  label: string
  group?: string | null
  /** Why it applies ("Customer type is EXPORT"); record scope only. */
  why?: string
}

export interface UdaRowResolution {
  _key: string | number | null
  drivers: Record<string, string | null>
  set_keys: string[]
  attr_keys: string[]
}

/** The `uda_resolve` read action's answer (plan §2.4). */
export interface UdaResolution {
  ok: boolean
  def_version: number
  /** Fields whose change re-decides the sets (`header.<col>` for the header's). */
  deciding_fields: string[]
  drivers: Record<string, string | null>
  columns: UdaColumnDef[]
  sets: UdaSetInfo[]
  rows: UdaRowResolution[]
  flags: UdaFlag[]
}

export interface UdaResolveRequest {
  form_no: string
  header?: Record<string, unknown>
  header_values?: Record<string, unknown>
  rows?: Array<Record<string, unknown> & { _key: string | number }>
  scope?: 'record' | 'rows' | 'list'
  /** An existing record (viewers resolve with `load`). */
  txn_id?: string
}

// ── the setup screen's calls (plan §2.5) ────────────────────────────────────

export interface UdaGetResult {
  obj_name: string
  design: UdaLayer
  draft: UdaLayer
  published: UdaLayer
  effective_draft: UdaLayer
  /** The draft revision — the `base_version` every write quotes. */
  version: number
  published_version: number
  changes: string[]
  limits: { max_drivers: number; max_driver_values: number; grid_row_page: number;
    grid_col_page: number }
  /** The data types an attribute may have (the type registry, in picker
   *  order, without the categories an attribute cannot have). */
  column_types?: UdaColumnType[]
}

/** One data type from the engine's type registry. */
export interface UdaColumnType {
  name: string
  size_kind: 'none' | 'length' | 'precision_scale' | string
  category: string
  aliases?: string[]
}

export interface UdaPatchResult {
  draft: UdaLayer
  version: number
  /** Warnings (a value the master lacks, an unchecked function). */
  problems: string[]
}

export interface UdaMasterValues {
  /** `[code, label, record count]`, at most 501 — `truncated` says "more". */
  values: Array<[string, string, number]>
  total: number
  truncated: boolean
  groups?: Array<{ label: string; values: string[] }>
}

export interface UdaCoverageRequest {
  form_no: string
  body?: UdaLayer
  rows_axis: string
  cols_axis?: string | null
  page_value?: string | null
  row_offset?: number
  row_limit?: number
  col_offset?: number
  col_limit?: number
  only_gaps?: boolean
}

export interface UdaCoverage {
  cells: Array<[string, string | null, string[]]>
  totals: { all: number; covered: number; none: number }
  rows: string[]
  cols: string[]
  row_total: number
  col_total: number
  page_driver: string | null
}

export interface UdaHistoryEntry {
  version: number
  summary: string
  published_by?: string | null
  published_at?: string | null
}

export interface UdaImportResult {
  applied: number
  skipped: Array<{ row: number; reason: string }>
  version: number
  problems: string[]
}

export interface UdaExportResult {
  rows: string[][]
  csv: string
  filename: string
}

/** One result line of a function's check: `ok` / `w` (warning) / `e` (error). */
export type UdaCodeResult = [string, string]

/** A function's check status: `checked` (its current source passed, against
 *  the attributes it reads now), `failed` (an error), `unchecked` (never
 *  checked, or its source changed since), `needs_recheck` (checked, but the
 *  attributes it reads changed since). Publishing needs `checked`. */
export type UdaCodeStatus = 'checked' | 'failed' | 'unchecked' | 'needs_recheck'

/** `check-code`: the results, stored with the source on the server. */
export interface UdaCheckResult {
  fn_name?: string
  results: UdaCodeResult[]
  status?: UdaCodeStatus
}

/** `get-code`: the organization's function as stored (draft, else published). */
export interface UdaCodeInfo {
  fn_name: string
  exists: boolean
  source: string | null
  status: UdaCodeStatus | null
  results: UdaCodeResult[]
  checked_at?: string | null
  published: boolean
  published_same: boolean
  limits?: { code_source_max?: number }
}

/** `save-code`: the stored source's status (a changed source is unchecked). */
export interface UdaSaveCodeResult {
  fn_name: string
  status: UdaCodeStatus
  source_hash: string
  results: UdaCodeResult[]
}

/** A refused write: 422 UDA_CUSTOMIZATION_NOT_ALLOWED (details.errors), 409
 *  UDA_CONFLICT (current_version), 501 UDA_CODE_NOT_AVAILABLE. */
export interface UdaErrorDetail {
  code: string
  message: string
  details?: { errors?: string[]; warnings?: string[] }
  current_version?: number
}

/** What a host gives the shared setup screen and runtime. Every call is bound
 *  to one project by the host; where things are stored is the server's
 *  decision (the caller's tenant). */
export interface UdaTransport {
  canSetup(objName: string): Promise<boolean>
  get(objName: string): Promise<UdaGetResult>
  patchDraft(objName: string, body: UdaLayer, baseVersion: number): Promise<UdaPatchResult>
  masterValues(objName: string, formNo: string, via: string, column: string,
    opts?: { q?: string; limit?: number }): Promise<UdaMasterValues>
  coverage(objName: string, req: UdaCoverageRequest): Promise<UdaCoverage>
  preview(objName: string, formNo: string, drivers: Record<string, string | null>,
    body?: UdaLayer): Promise<UdaResolution>
  /** Compile and check a function (stored with its results). */
  checkCode(objName: string, fnName: string, source: string): Promise<UdaCheckResult>
  /** The organization's function as stored. */
  getCode(objName: string, fnName: string): Promise<UdaCodeInfo>
  /** Save a function's source (draft; a changed source needs a new check). */
  saveCode(objName: string, fnName: string, source: string): Promise<UdaSaveCodeResult>
  publish(objName: string, baseVersion: number, summary?: string): Promise<{ version: number; problems?: string[] }>
  history(objName: string): Promise<{ versions: UdaHistoryEntry[] }>
  rollback(objName: string, version: number): Promise<{ version: number; draft_version: number }>
  importRules(objName: string, formNo: string, input: { rows?: string[][]; fileBase64?: string;
    filename?: string; mode?: 'replace' | 'append'; baseVersion?: number }): Promise<UdaImportResult>
  exportRules(objName: string, formNo: string): Promise<UdaExportResult>
  catalogTables(objName: string): Promise<{ tables: Array<{ name: string }> }>
  catalogColumns(objName: string, table: string): Promise<{ columns: Array<{ column_name: string; data_type?: string }> }>
}

/** The JSON body every setup call posts (`obj_name` + the call's fields) —
 *  one place, so the hosts' transports cannot drift. */
export function udaBody(objName: string, extra: Record<string, unknown> = {}): Record<string, unknown> {
  return { obj_name: objName, ...extra }
}

/** The import call's body (camelCase options → the server's names). */
export function udaImportBody(objName: string, formNo: string, input: {
  rows?: string[][]; fileBase64?: string; filename?: string; mode?: 'replace' | 'append';
  baseVersion?: number }): Record<string, unknown> {
  return udaBody(objName, {
    form_no: formNo, rows: input.rows, file_base64: input.fileBase64,
    filename: input.filename, mode: input.mode, base_version: input.baseVersion,
  })
}
